import { __spreadArray, __assign } from 'tslib';
import { useCombobox } from 'downshift';
import React, { useState, useMemo, useCallback } from 'react';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { noop } from '../../utils/noop.esm.js';
import '@popperjs/core';
import { Portal } from '../Portal/Portal.esm.js';
import { SearchInput } from './SearchInput.esm.js';
import { SelectList } from './SelectList.esm.js';
import { useSelectAll } from './useSelectAll.esm.js';
import { sortBySelected, onSelectedItemChange } from './utils.esm.js';

var emptyItems = [];
var defaultFilterFn = function (filter, value) {
    return value.toLowerCase().includes(filter.toLowerCase());
};
var defaultItemToString = function (item) {
    return item ? String(item.value) : "";
};
/**
 * This is the dropdown for the select with Search.
 * It does not control opening and should only be rendered when it is supposed to be open.
 */
function Dropdown(_a) {
    var containerRef = _a.containerRef, anchorRef = _a.anchorRef, dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, _b = _a.items, items = _b === void 0 ? emptyItems : _b, _c = _a.filterFn, filterFn = _c === void 0 ? defaultFilterFn : _c, isOpen = _a.isOpen, _d = _a.itemToString, itemToString = _d === void 0 ? defaultItemToString : _d, _e = _a.enableMultiSelect, enableMultiSelect = _e === void 0 ? false : _e, _f = _a.enableSelectAll, enableSelectAll = _f === void 0 ? false : _f, _g = _a.isLoading, isLoading = _g === void 0 ? false : _g, labelId = _a.labelId, darkMode = _a.darkMode, menuStyle = _a.menuStyle, _h = _a.onChange, onChange = _h === void 0 ? noop : _h, onMenuKeyEnter = _a.onMenuKeyEnter, onOpenChange = _a.onOpenChange, onSearch = _a.onSearch, selectedItem = _a.selectedItem, toggleButtonId = _a.toggleButtonId, formatOption = _a.formatOption, fitToItemWidth = _a.fitToItemWidth, fullWidth = _a.fullWidth;
    var _j = useState(""), filter = _j[0], setFilter = _j[1];
    var selectedItems = selectedItem ? [selectedItem].flat() : [];
    var selectedValues = useMemo(function () { return selectedItems.map(function (item) { return item.value; }); }, [selectedItems]);
    var filteredItems = useMemo(function () {
        var filtered = __spreadArray([], items, true);
        if (filter && !onSearch) {
            filtered = filtered.filter(function (item) {
                return filterFn(filter, itemToString(item));
            });
        }
        if (enableMultiSelect)
            filtered = filtered.sort(sortBySelected(selectedValues));
        return filtered;
    }, [items, filter]);
    var _k = useSelectAll({
        enableSelectAll: enableSelectAll,
        items: filteredItems,
        selectedValues: selectedValues,
        enableMultiSelect: enableMultiSelect
    }), showSelectAll = _k.showSelectAll, selectAllItem = _k.selectAllItem, allSelected = _k.allSelected;
    var normalizedItems = enableSelectAll && showSelectAll
        ? __spreadArray([selectAllItem], filteredItems, true) : filteredItems;
    var filterItems = useCallback(function (value) {
        setFilter(value !== null && value !== void 0 ? value : "");
        onSearch === null || onSearch === void 0 ? void 0 : onSearch(value !== null && value !== void 0 ? value : "");
    }, [onSearch, items]);
    var _l = useCombobox({
        isOpen: true,
        items: normalizedItems,
        inputValue: filter,
        itemToString: itemToString,
        labelId: labelId,
        toggleButtonId: toggleButtonId,
        onStateChange: function (action) {
            var _a, _b;
            var type = action.type;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (!enableMultiSelect) {
                        (_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                        onOpenChange(false);
                    }
                    break;
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                case useCombobox.stateChangeTypes.InputBlur:
                    (_b = anchorRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                    onOpenChange(false);
                    filterItems("");
                    break;
            }
        },
        stateReducer: function (state, actionAndChanges) {
            var changes = actionAndChanges.changes, type = actionAndChanges.type;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    return __assign(__assign({}, changes), { highlightedIndex: state.highlightedIndex });
                default:
                    return changes;
            }
        },
        selectedItem: enableMultiSelect ? null : selectedItems[0] || null,
        onSelectedItemChange: function (changes) {
            onSelectedItemChange({
                changes: changes,
                enableMultiSelect: enableMultiSelect,
                selectedItems: selectedItems,
                onChange: onChange,
                selectedValues: selectedValues,
                selectAllItem: selectAllItem,
                allSelected: allSelected,
                itemsForSelectAll: filteredItems
            });
        }
    }), getComboboxProps = _l.getComboboxProps, getInputProps = _l.getInputProps, getItemProps = _l.getItemProps, getMenuProps = _l.getMenuProps, highlightedIndex = _l.highlightedIndex;
    return (React.createElement(Portal, null,
        React.createElement("div", __assign({}, getComboboxProps({ ref: containerRef }), { className: classNames("tw-z-30", darkMode && "dark") }),
            React.createElement(SelectList, { dataMetrics: dataMetrics, dataTestId: dataTestId, isOpen: isOpen, menuStyle: menuStyle, getMenuProps: getMenuProps, onMenuKeyEnter: onMenuKeyEnter, items: normalizedItems, highlightedIndex: highlightedIndex, selectedItems: selectedItems, selectAllItem: selectAllItem, enableMultiSelect: enableMultiSelect, getItemProps: getItemProps, showSelectAll: showSelectAll, search: React.createElement(SearchInput, { isLoading: isLoading, filter: filter, filterItems: filterItems, getInputProps: getInputProps }), selectedValues: selectedValues, formatOption: formatOption, fitToItemWidth: fullWidth ? false : fitToItemWidth }))));
}

export { Dropdown };
//# sourceMappingURL=Dropdown.esm.js.map
